// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getEmailTypesHandler from "@pacific/App/Handlers/CommunicationAndPreferences/GetEmailTypesHandler";
import getInterestCategoriesHandler from "@pacific/App/Handlers/CommunicationAndPreferences/GetInterestCategoriesHandler";
import savePreferencesHandler from "@pacific/App/Handlers/CommunicationAndPreferences/SavePreferencesHandler";
import subscribe from "@pacific/App/Handlers/MailchimpHandler/Subscribe";
import { xIsAdminstrator } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { fontConfig } from "@pacific/XDL/PreStyle/typography";
import { IneterestCategoriesModel, IneterestCategoryModel, InterestModel } from "@pacific/XDL/types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    savePreferencesHandler,
    getEmailTypesHandler,
    getInterestCategoriesHandler,
    subscribe,
};

const mapStateToProps = (state: ApplicationState) => ({
    isAdminstrator: xIsAdminstrator(state),
});

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasToasterContext;

export interface CommunicationAndPreferencesStyles {
    container?: InjectableCss;
    sectionWrapper?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    ecommerceSectionWrapper?: InjectableCss;
    checkboxGroup?: CheckboxGroupProps;
    checkbox?: CheckboxPresentationProps;
    adminOnly?: TypographyPresentationProps;
    saveButton?: ButtonPresentationProps;
}

export const communicationAndPreferencesStyles: CommunicationAndPreferencesStyles = {
    sectionWrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    sectionTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 22px;
            line-height: 20px;
        `,
    },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },
    adminOnly: {
        css: css`
            &&& {
                font-size: 12px;
                font-weight: ${fontConfig.weight.medium};
                margin-left: 18px;
                margin-bottom: 10px;
            }
        `,
    },
};

const styles = communicationAndPreferencesStyles;

const CommunicationAndPreferences = ({
    savePreferencesHandler,
    getEmailTypesHandler,
    getInterestCategoriesHandler,
    subscribe,
    isAdminstrator,
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    const [subscribeToOrderConfirmationEmails, setSubscribeToOrderConfirmationEmails] = useState(false);
    const [subscribeToRegistrationEmails, setSubscribeToRegistrationEmails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmittingSaving, setIsSubmittingSaving] = useState(false);
    const [isSubmittingSubscription, setIsSubmittingSubscription] = useState(false);

    const [interestsCategories, setInterestsCategories] = useState<IneterestCategoryModel[]>();

    const [, updateState] = React.useState();
    const forceUpdate = React.useCallback(() => updateState({} as any), []);

    useEffect(() => {
        if (!isSubmitting) {
            getEmailTypesHandler({
                onSuccess: (result: any) => {
                    if (result) {
                        result.emailTypes?.includes("OrderConfirmationEmails")
                            ? setSubscribeToOrderConfirmationEmails(true)
                            : setSubscribeToOrderConfirmationEmails(false);
                        result.emailTypes?.includes("RegistrationEmails")
                            ? setSubscribeToRegistrationEmails(true)
                            : setSubscribeToRegistrationEmails(false);
                    }
                },
            });
            getInterestCategoriesHandler({
                onSuccess: (result: IneterestCategoriesModel) => {
                    if (result) {
                        setInterestsCategories(result.interestCategories);
                    }
                },
            });
        }
    }, [isSubmitting]); // this will trigger updating the field values after submit is done/initial page load.

    useEffect(() => {
        if (isSubmitting && !isSubmittingSaving && !isSubmittingSubscription) {
            setIsSubmitting(false);
        }
    }, [isSubmittingSaving, isSubmittingSubscription]);

    const communicationAndPreferencesSaveingErrorMessage = siteMessage(
        "Custom_CommunicationAndPreferences_Error_Message",
    );

    const onSubscribeToRegistrationEmailsChange = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setSubscribeToRegistrationEmails(value);
    };
    const onSubscribeToOrderConfirmationEmailsChange = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setSubscribeToOrderConfirmationEmails(value);
    };

    const onSubscribeToInterestChange = (
        value: boolean,
        interestName: string,
        interestId: string,
        interest: InterestModel,
    ) => {
        const newInterestCategories = interestsCategories;
        newInterestCategories?.forEach(interestCategory => {
            if (interestCategory.name === interestName) {
                interestCategory.interests.forEach(interest => {
                    if (interest.id === interestId) {
                        interest.subscribed = value;
                    }
                });
            }
        });

        setInterestsCategories(newInterestCategories);
        forceUpdate();
    };

    const handleSave = () => {
        setIsSubmitting(true);
        const ids: string[] = [];
        interestsCategories?.forEach(interestCategory => {
            interestCategory.interests.forEach(interest => {
                if (interest.subscribed === true) {
                    ids.push(interest.id);
                }
            });
        });

        setIsSubmittingSubscription(true);
        subscribe({
            subscribeToLists: ids || [],
            subscribeToAllLists: false,
            onSuccess: () => {
                setIsSubmittingSubscription(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });

        setIsSubmittingSaving(true);
        savePreferencesHandler({
            subscribeToRegistrationEmails,
            subscribeToOrderConfirmationEmails,
            onError: (e: any) => {
                setIsSubmittingSaving(false);
                toasterContext.addToast({
                    body: e || communicationAndPreferencesSaveingErrorMessage,
                    messageType: "danger",
                });
            },
            onSuccess: () => {
                setIsSubmittingSaving(false);
                toasterContext.addToast({
                    body: siteMessage("Custom_CommunicationAndPreferences_SuccessMessage"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
            },
        });
    };

    return (
        <StyledWrapper {...styles.container}>
            {interestsCategories &&
                interestsCategories.map(interestCategory => (
                    <StyledWrapper key={interestCategory.name} {...styles.sectionWrapper}>
                        <Typography {...styles.sectionTitle}>{interestCategory.name}</Typography>
                        <CheckboxGroup {...styles.checkboxGroup}>
                            {interestCategory.interests &&
                                interestCategory.interests.length > 0 &&
                                interestCategory.interests.map(interest => (
                                    <Checkbox
                                        key={interest.id}
                                        {...styles.checkbox}
                                        checked={interest.subscribed}
                                        onChange={(e, value) => {
                                            onSubscribeToInterestChange(
                                                value,
                                                interestCategory.name,
                                                interest.id,
                                                interest,
                                            );
                                        }}
                                        data-test-selector={`communicationAndPreferences_${interest.id}`}
                                    >
                                        {interest.name}
                                    </Checkbox>
                                ))}
                        </CheckboxGroup>
                    </StyledWrapper>
                ))}
            {isAdminstrator && (
                <StyledWrapper {...styles.sectionWrapper}>
                    <Typography {...styles.sectionTitle}>{translate("Ecommerce Communications")}</Typography>
                    <CheckboxGroup {...styles.checkboxGroup}>
                        <Checkbox
                            {...styles.checkbox}
                            checked={subscribeToRegistrationEmails}
                            onChange={onSubscribeToRegistrationEmailsChange}
                            data-test-selector="communicationAndPreferences_registrationEmails"
                        >
                            {translate("Registration Emails")}
                        </Checkbox>
                        <Typography {...styles.adminOnly}>{translate("Admin Only")}</Typography>
                        <Checkbox
                            {...styles.checkbox}
                            checked={subscribeToOrderConfirmationEmails}
                            onChange={onSubscribeToOrderConfirmationEmailsChange}
                            data-test-selector="communicationAndPreferences_orderConfirmationEmails"
                        >
                            {translate("Order Confirmation Emails")}
                        </Checkbox>
                        <Typography {...styles.adminOnly}>{translate("Admin Only")}</Typography>
                    </CheckboxGroup>
                </StyledWrapper>
            )}
            <Button {...styles.saveButton} aria-label="Save Preferences" onClick={handleSave} disabled={isSubmitting}>
                {translate("Save Preferences")}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(CommunicationAndPreferences)),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Communication and Preferences",
        allowedContexts: ["CommunicationAndPreferencesPage"] as any,
    },
};

export default widgetModule;
